import { useState } from "react";
import axios from "axios";
import {
  Phone,
  Mail,
  MapPin,
  Printer,
  Calendar,
  Send,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { SITE } from "@/lib/site";
import useReveal from "@/lib/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SSN_RE = /\b\d{3}[-\s.]\d{2}[-\s.]\d{4}\b|\b\d{9}\b/;
const SSN_MSG =
  "For your protection, please remove what looks like a Social Security or tax ID number. Never send SSNs or account numbers through this form — call our office instead.";

export default function Contact() {
  useReveal();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "General Inquiry",
    message: "",
    website: "", // honeypot — must stay empty
  });
  const [status, setStatus] = useState({ state: "idle", msg: "" }); // idle | loading | success | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: "Please fill in name, email and message." });
      return;
    }
    if (SSN_RE.test(`${form.name} ${form.subject} ${form.message}`)) {
      setStatus({ state: "error", msg: SSN_MSG });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/contact`, form);
      setStatus({
        state: "success",
        msg: "Thanks! Your message was sent. We’ll respond within 48 business hours.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "General Inquiry",
        message: "",
        website: "",
      });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please call our office or try again.";
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Submission failed." });
    }
  };

  return (
    <main data-testid="page-contact">
      <section className="pt-16 md:pt-20 pb-10 md:pb-14 bg-gradient-to-b from-navy-50/40 to-transparent">
        <div className="container-narrow">
          <div className="text-[12px] tracking-[0.22em] uppercase text-gold-500 font-medium reveal">
            Contact us
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl text-navy-800 mt-3 leading-[1.04] tracking-tight reveal">
            We’d love to hear from you.
          </h1>
          <p className="mt-5 max-w-3xl text-slate-600 text-[17px] leading-relaxed reveal">
            Send us a message, give us a call, or schedule a free consultation. We typically respond within 48 business hours.
          </p>
        </div>
      </section>

      <section className="py-10 md:py-16">
        <div className="container-narrow grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Form */}
          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-3xl border border-navy-100 bg-white p-7 md:p-10"
              noValidate
            >
              <h2 className="font-serif-display text-2xl md:text-3xl text-navy-800 leading-tight">
                Send a message
              </h2>
              <p className="text-slate-500 text-[14.5px] mt-2">
                Fields marked * are required.
              </p>

              <div
                role="note"
                data-testid="contact-security-notice"
                className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-[13.5px] leading-relaxed"
              >
                <strong>Please do not send sensitive information through this form.</strong> Do not include Social Security numbers, dates of birth, driver's license numbers, bank or routing numbers, or tax return copies. Call our office at <a href={SITE.phoneTel} className="underline font-medium">{SITE.phone}</a> and we will arrange a secure way to share documents.
              </div>

              {/* Honeypot (hidden from users, visible to bots) */}
              <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-0 w-0 overflow-hidden" style={{ position: "absolute" }}>
                <label htmlFor="contact-website">Website</label>
                <input
                  id="contact-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={update("website")}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <div>
                  <label htmlFor="contact-name" className="text-[13px] font-medium text-slate-600">
                    Name *
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    autoComplete="name"
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-navy-100 focus:border-navy-500 focus:ring-2 focus:ring-navy-100 outline-none bg-white text-slate-800"
                    placeholder="Your full name"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-[13px] font-medium text-slate-600">
                    Email *
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    autoComplete="email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-navy-100 focus:border-navy-500 focus:ring-2 focus:ring-navy-100 outline-none bg-white text-slate-800"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="text-[13px] font-medium text-slate-600">
                    Phone
                  </label>
                  <input
                    id="contact-phone"
                    name="phone"
                    autoComplete="tel"
                    data-testid="contact-input-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-navy-100 focus:border-navy-500 focus:ring-2 focus:ring-navy-100 outline-none bg-white text-slate-800"
                    placeholder="(918) 555-0123"
                  />
                </div>
                <div>
                  <label htmlFor="contact-subject" className="text-[13px] font-medium text-slate-600">
                    Subject
                  </label>
                  <select
                    id="contact-subject"
                    name="subject"
                    data-testid="contact-input-subject"
                    value={form.subject}
                    onChange={update("subject")}
                    className="mt-1.5 w-full px-4 py-3 rounded-xl border border-navy-100 focus:border-navy-500 focus:ring-2 focus:ring-navy-100 outline-none bg-white text-slate-800"
                  >
                    <option>General Inquiry</option>
                    <option>New Client Inquiry</option>
                    <option>Tax Planning & Preparation</option>
                    <option>Business Accounting & Payroll</option>
                    <option>Insurance Services</option>
                    <option>Financial Planning & Wealth Management</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="contact-message" className="text-[13px] font-medium text-slate-600">
                  Message *
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  data-testid="contact-input-message"
                  rows={6}
                  value={form.message}
                  onChange={update("message")}
                  className="mt-1.5 w-full px-4 py-3 rounded-xl border border-navy-100 focus:border-navy-500 focus:ring-2 focus:ring-navy-100 outline-none bg-white text-slate-800 resize-y"
                  placeholder="How can we help?"
                  required
                />
              </div>

              {status.state === "success" && (
                <div
                  data-testid="contact-success"
                  role="status"
                  aria-live="polite"
                  className="mt-5 flex items-start gap-3 rounded-xl bg-gold-50 border border-gold-200 px-4 py-3 text-gold-700"
                >
                  <CheckCircle2 size={18} className="mt-0.5" />
                  <p className="text-[14px]">{status.msg}</p>
                </div>
              )}
              {status.state === "error" && (
                <div
                  data-testid="contact-error"
                  role="alert"
                  className="mt-5 flex items-start gap-3 rounded-xl bg-red-50 border border-red-100 px-4 py-3 text-red-700"
                >
                  <AlertCircle size={18} className="mt-0.5" />
                  <p className="text-[14px]">{status.msg}</p>
                </div>
              )}

              <div className="mt-6 flex items-center gap-3">
                <button
                  type="submit"
                  disabled={status.state === "loading"}
                  data-testid="contact-submit"
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <Send size={16} />
                  {status.state === "loading" ? "Sending…" : "Send message"}
                </button>
                <a
                  href={SITE.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-book-cta"
                  className="btn-secondary"
                >
                  <Calendar size={16} /> Book a consultation
                </a>
              </div>
            </form>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-5 reveal" data-testid="contact-info">
            <div className="rounded-3xl border border-navy-100 bg-gradient-to-br from-navy-800 to-navy-800 text-white p-7 md:p-10">
              <div className="text-[12px] tracking-[0.22em] uppercase text-gold-300">
                Office
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl mt-3">
                Visit, call or email us.
              </h3>

              <ul className="mt-7 space-y-4 text-[15px]">
                <li className="flex gap-3">
                  <MapPin size={18} className="text-gold-300 mt-0.5 shrink-0" />
                  <a
                    href={SITE.address.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-address"
                    className="text-navy-50/90 hover:text-white"
                  >
                    {SITE.address.street}
                    <br />
                    {SITE.address.cityStateZip}
                  </a>
                </li>
                <li className="flex gap-3">
                  <Phone size={18} className="text-gold-300 mt-0.5 shrink-0" />
                  <a
                    href={SITE.phoneTel}
                    data-testid="contact-phone-link"
                    className="text-navy-50/90 hover:text-white"
                  >
                    {SITE.phone}
                  </a>
                </li>
                <li className="flex gap-3">
                  <Printer size={18} className="text-gold-300 mt-0.5 shrink-0" />
                  <span className="text-navy-50/90">Fax: {SITE.fax}</span>
                </li>
                <li className="flex gap-3">
                  <Mail size={18} className="text-gold-300 mt-0.5 shrink-0" />
                  <a
                    href={`mailto:${SITE.email}`}
                    data-testid="contact-email-link"
                    className="text-navy-50/90 hover:text-white break-all"
                  >
                    {SITE.email}
                  </a>
                </li>
              </ul>

              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-info-book"
                className="btn-primary !bg-white !text-navy-800 hover:!bg-navy-50 mt-8"
              >
                <Calendar size={16} /> Book a Free Consultation
              </a>
            </div>

            <div className="mt-5 rounded-3xl overflow-hidden border border-navy-100 bg-white">
              <iframe
                title="Office location map"
                src="https://www.google.com/maps?q=9500+N+129th+E+Ave+%23123,+Owasso,+OK+74055&output=embed"
                className="w-full h-[280px] border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
